import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import QRScanner from "@/components/QRScanner";
import { DeptUserPicker } from "@/components/work/WorkParts";

export function ScanDialogs({ scanOpen, setScanOpen, onScan, scanResult, setScanResult, onOpenBill }) {
  return (
    <>
      <Dialog open={scanOpen} onOpenChange={setScanOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Scan QR / Barcode</DialogTitle><DialogDescription className="sr-only">Scan an order, SKU, location, or task code</DialogDescription></DialogHeader>
          {scanOpen && <QRScanner onResult={onScan} />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!scanResult} onOpenChange={() => setScanResult(null)}>
        <DialogContent className="bg-card border-border" data-testid="scan-result-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Scan Result — {scanResult?.type}</DialogTitle><DialogDescription className="sr-only">Review the scanned record result</DialogDescription></DialogHeader>
          {scanResult?.type === "bill" && (
            <div className="space-y-2 text-sm">
              <p className="metric text-primary font-bold text-lg">{scanResult.data.bill_no}</p>
              <p>{scanResult.data.customer} · {scanResult.data.item_name}</p>
              <p className="metric">{scanResult.data.quantity.toLocaleString()} pcs · {scanResult.data.cartons ?? "—"} cartons</p>
              <p className="text-muted-foreground">Packing: {scanResult.data.packing_type || "—"}</p>
              <Button className="w-full bg-primary text-primary-foreground" onClick={() => { setScanResult(null); onOpenBill(scanResult.data.bill_no); }} data-testid="scan-open-bill">
                Open Bill
              </Button>
            </div>
          )}
          {scanResult?.type === "location" && (
            <div className="space-y-2 text-sm font-mono">
              <p className="text-primary font-bold">{scanResult.data.bin}</p>
              <p>{scanResult.data.warehouse} → {scanResult.data.zone} → {scanResult.data.rack} → {scanResult.data.shelf}</p>
              <p>SKU {scanResult.data.sku} · Batch {scanResult.data.batch || "—"} · Qty {scanResult.data.quantity}</p>
            </div>
          )}
          {scanResult?.type === "sku" && (
            <div className="space-y-2 text-sm font-mono">
              {scanResult.data.map((l) => (
                <p key={l.bin}>{l.bin} — qty {l.quantity} (batch {l.batch || "—"})</p>
              ))}
            </div>
          )}
          {scanResult?.type === "task" && (
            <div className="space-y-2 text-sm">
              <p className="metric text-primary font-bold">{scanResult.data.task_id}</p>
              <p className="capitalize">{scanResult.data.type} · {scanResult.data.status}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export function TaskDialogs({
  meta, completeTask, setCompleteTask, completeForm, setCompleteForm, completeError, submitComplete,
  binOptions = [], pickAllocs = [], setPickAllocs = () => {},
  problemTask, setProblemTask, problemForm, setProblemForm, submitProblem,
  assignTarget, setAssignTarget, assignableUsers, assignSel, setAssignSel, submitAssignExisting,
  assignOpen, setAssignOpen, assignForm, setAssignForm, bills, users, departments = [], submitAssign,
}) {
  return (
    <>
      <Dialog open={!!completeTask} onOpenChange={() => setCompleteTask(null)}>
        <DialogContent className="bg-card border-border" data-testid="complete-task-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Complete {completeTask?.task_id}</DialogTitle><DialogDescription className="sr-only">Complete task and record quantity or timing details</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Quantity handled (pcs) *</Label>
              <Input data-testid="complete-quantity-input" type="number" value={completeForm.quantity} onChange={(e) => setCompleteForm({ ...completeForm, quantity: e.target.value })} />
            </div>
            <div>
              <Label>Cartons</Label>
              <Input data-testid="complete-cartons-input" type="number" value={completeForm.cartons} onChange={(e) => setCompleteForm({ ...completeForm, cartons: e.target.value })} />
            </div>
            {completeTask?.type === "putaway" && (
              <div>
                <Label>Destination Bin *</Label>
                <Select value={completeForm.bin || ""} onValueChange={(v) => setCompleteForm({ ...completeForm, bin: v })}>
                  <SelectTrigger data-testid="putaway-bin-select"><SelectValue placeholder="Select destination bin" /></SelectTrigger>
                  <SelectContent>
                    {binOptions.map((l) => (
                      <SelectItem key={l.bin} value={l.bin} data-testid={`putaway-bin-${l.bin}`}>
                        {l.bin} — {l.warehouse}{l.sku ? ` (${l.sku}: ${l.quantity})` : " (empty)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {completeTask?.type === "picking" && pickAllocs.length > 0 && (
              <div className="space-y-1.5" data-testid="pick-allocations">
                <Label>Bin allocations (FIFO suggested — confirm or adjust)</Label>
                {pickAllocs.map((a, i) => (
                  <div key={a.bin} className="flex items-center gap-2">
                    <span className="text-xs font-mono flex-1">{a.bin} <span className="text-muted-foreground">(bin has {a.bin_qty})</span></span>
                    <Input className="w-24 h-8" type="number" min="0" max={a.bin_qty} value={a.qty} data-testid={`pick-alloc-qty-${a.bin}`}
                      onChange={(e) => setPickAllocs(pickAllocs.map((x, xi) => (xi === i ? { ...x, qty: e.target.value } : x)))} />
                  </div>
                ))}
                <p className="text-xs text-muted-foreground" data-testid="pick-alloc-total">
                  Allocated {pickAllocs.reduce((s, a) => s + (parseInt(a.qty) || 0), 0)} pcs — must equal quantity handled
                </p>
              </div>
            )}
            <div>
              <Label>Delay reason (required if over standard time)</Label>
              <Select value={completeForm.delay_reason} onValueChange={(v) => setCompleteForm({ ...completeForm, delay_reason: v })}>
                <SelectTrigger data-testid="complete-delay-reason-select"><SelectValue placeholder="Select reason if delayed" /></SelectTrigger>
                <SelectContent>
                  {meta.delay_reasons.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Remarks</Label>
              <Textarea data-testid="complete-remarks-input" value={completeForm.remarks} onChange={(e) => setCompleteForm({ ...completeForm, remarks: e.target.value })} />
            </div>
            {completeError && <p className="text-sm text-signal-red" data-testid="complete-error">{completeError}</p>}
            <Button className="w-full h-12 text-base font-bold bg-signal-green hover:bg-signal-green/90 text-white" onClick={submitComplete} data-testid="complete-submit-button">
              CONFIRM COMPLETE
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!problemTask} onOpenChange={() => setProblemTask(null)}>
        <DialogContent className="bg-card border-border" data-testid="problem-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Report Problem — {problemTask?.task_id}</DialogTitle><DialogDescription className="sr-only">Report a task problem to the supervisor</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Category</Label>
              <Select value={problemForm.delay_reason} onValueChange={(v) => setProblemForm({ ...problemForm, delay_reason: v })}>
                <SelectTrigger data-testid="problem-reason-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {meta.delay_reasons.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Describe the problem</Label>
              <Textarea data-testid="problem-text-input" value={problemForm.problem} onChange={(e) => setProblemForm({ ...problemForm, problem: e.target.value })} />
            </div>
            <Button className="w-full h-12 bg-signal-red hover:bg-signal-red/90 text-white font-bold" onClick={submitProblem} data-testid="problem-submit-button">
              SEND TO SUPERVISOR
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!assignTarget} onOpenChange={() => setAssignTarget(null)}>
        <DialogContent className="bg-card border-border" data-testid="assign-existing-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Assign Workers — {assignTarget?.task_id}</DialogTitle><DialogDescription className="sr-only">Assign available users to an existing task</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground capitalize">{assignTarget?.type} {assignTarget?.bill_no && <>· <span className="metric text-primary">{assignTarget.bill_no}</span></>} · {assignTarget?.quantity?.toLocaleString()} pcs</p>
            <div className="flex flex-wrap gap-2" data-testid="assign-existing-workers">
              {assignableUsers.map((u) => {
                const sel = assignSel.includes(u.user_id);
                return (
                  <button
                    key={u.user_id}
                    type="button"
                    data-testid={`assign-existing-worker-${u.user_id}`}
                    onClick={() => setAssignSel(sel ? assignSel.filter((x) => x !== u.user_id) : [...assignSel, u.user_id])}
                    className={`px-3 py-2 rounded-md text-sm border transition-colors duration-150 ${sel ? "bg-primary/20 border-primary text-primary" : "border-border text-muted-foreground hover:bg-accent"}`}
                  >
                    {u.name}
                  </button>
                );
              })}
            </div>
            <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" disabled={assignSel.length === 0} onClick={submitAssignExisting} data-testid="assign-existing-submit">Assign</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="bg-card border-border" data-testid="assign-task-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Assign Task</DialogTitle><DialogDescription className="sr-only">Create and assign a new operational task</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={assignForm.type} onValueChange={(v) => setAssignForm({ ...assignForm, type: v })}>
                  <SelectTrigger data-testid="assign-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(meta.task_types || []).map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Bill (optional)</Label>
                <Select value={assignForm.bill_no} onValueChange={(v) => setAssignForm({ ...assignForm, bill_no: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="assign-bill-select"><SelectValue placeholder="No bill" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No bill</SelectItem>
                    {bills.filter((b) => b.status !== "dispatched").map((b) => (
                      <SelectItem key={b.bill_no} value={b.bill_no}>{b.bill_no} — {b.customer}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 p-3 rounded-md border border-primary/30 bg-primary/5" data-testid="assign-owner-block">
              <div>
                <Label>Department *</Label>
                <select className="h-9 mt-1 rounded-md border border-input bg-background px-2 text-xs w-full" value={assignForm.department || ""} data-testid="assign-department-select"
                  onChange={(e) => setAssignForm({ ...assignForm, department: e.target.value, assigned_user_id: "" })}>
                  <option value="">Select department…</option>
                  {departments.map((d) => <option key={d.name} value={d.name}>{d.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Responsible user *</Label>
                <div className="mt-1"><DeptUserPicker department={assignForm.department} value={assignForm.assigned_user_id} testid="assign-responsible-user"
                  onChange={(v) => setAssignForm({ ...assignForm, assigned_user_id: v, assigned_to: v && !assignForm.assigned_to.includes(v) ? [v, ...assignForm.assigned_to] : assignForm.assigned_to })} /></div>
              </div>
            </div>
            <div>
              <Label>Additional workers</Label>
              <div className="flex flex-wrap gap-2 mt-1" data-testid="assign-workers-list">
                {users.map((u) => {
                  const sel = assignForm.assigned_to.includes(u.user_id);
                  return (
                    <button
                      key={u.user_id}
                      type="button"
                      data-testid={`assign-worker-${u.user_id}`}
                      onClick={() => setAssignForm({ ...assignForm, assigned_to: sel ? assignForm.assigned_to.filter((x) => x !== u.user_id) : [...assignForm.assigned_to, u.user_id] })}
                      className={`px-3 py-2 rounded-md text-sm border transition-colors duration-150 ${sel ? "bg-primary/20 border-primary text-primary" : "border-border text-muted-foreground hover:bg-accent"}`}
                    >
                      {u.name}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Quantity</Label>
                <Input data-testid="assign-quantity-input" type="number" value={assignForm.quantity} onChange={(e) => setAssignForm({ ...assignForm, quantity: e.target.value })} />
              </div>
              <div>
                <Label>Deadline (minutes from now)</Label>
                <Input data-testid="assign-deadline-input" type="number" value={assignForm.deadline_minutes} onChange={(e) => setAssignForm({ ...assignForm, deadline_minutes: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Location</Label>
              <Input data-testid="assign-location-input" placeholder="FG Warehouse / Zone A / Rack A01" value={assignForm.location} onChange={(e) => setAssignForm({ ...assignForm, location: e.target.value })} />
            </div>
            <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" onClick={submitAssign} data-testid="assign-submit-button">Assign</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
